import AttachmentEditor from '@components/AttachmentEditor'
import { Program } from '@models/membership/Program'
import {
  updateProgramWalletAsset,
  WalletAssetType,
} from '@services/membership/ProgramService'
import { useAppDispatch } from '@store/hooks'
import { getProgramWithSlug } from '@store/membership/ProgramReducer'
import { ExpectedImage } from '@utils/AttachmentFile'
import { requiredRule } from '@utils/rules'
import { Button, Card, Flex, Form, Space, Typography } from 'antd'
import { ReactNode, useEffect, useState } from 'react'
import { HIGHLIGHT_COLOR, useFieldHighlight } from './highlight'
import { FieldHighlightProps, WalletCardAsset, WalletField } from './types'
import WalletFieldTags, { WalletFieldTag } from './WalletFieldTags'

const { Title, Text } = Typography

interface Props extends FieldHighlightProps {
  title: ReactNode
  assetType: WalletAssetType
  /** Links the uploader to its region in the live preview. */
  field: WalletField
  /** Which pass surfaces use this image, shown as chips in the description. */
  tags?: WalletFieldTag[]
  /** Size hint shown in the description — worded by the caller since only
   *  logo/icon have a server-enforced minimum; strip/hero don't. */
  sizeHint: string
  /** Optional note on where the image lands / what it's for. */
  hint?: ReactNode
  /** Expected image dimensions/ratio — drives the crop/resize modal + the
   *  auto "Format recommandé" line in AttachmentEditor (from #528). */
  image?: ExpectedImage
  program: Program
  /** The master (scale 0) URL from `Program.walletAssets`, or null/undefined
   *  when this asset hasn't been uploaded yet — not an `Image`, the resource
   *  only ever exposes the bare URL for wallet assets. */
  current: string | null | undefined
  /** The locally picked (not yet saved) file, or the current URL — for the live preview. */
  onAssetChange?: (asset: WalletCardAsset) => void
}

/**
 * One wallet master image (logo, icon, strip or hero), saved on its own —
 * mirrors DesignBrandingTab's logo dragger, but refetches the program after a
 * successful upload (the perks-pdf pattern) so the "current" prop, and the
 * preview once it falls back to it, pick up the new URL. The tags + size hint
 * sit in a description card under the dragger, mirroring BookletUploader, and
 * the card links to the preview: hover it and the matching image region lights
 * up, and vice-versa.
 */
const WalletAssetUploader = ({
  title,
  assetType,
  field,
  tags,
  sizeHint,
  hint,
  image,
  program,
  current,
  onAssetChange,
  activeField,
  onActiveFieldChange,
}: Props) => {
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isActive, handlers } = useFieldHighlight(field, {
    activeField,
    onActiveFieldChange,
  })

  const fileList = Form.useWatch('file', form)

  useEffect(() => {
    form.setFieldsValue({
      file: current ? [{ name: `${assetType}.png`, url: current }] : [],
    })
  }, [current])

  useEffect(() => {
    const picked = fileList?.[0]
    onAssetChange?.(picked?.originFileObj ?? current ?? undefined)
  }, [fileList, current])

  const onFinish = (values: any) => {
    const file = values.file?.[0]?.originFileObj
    if (!file) {
      return
    }

    setIsLoading(true)
    updateProgramWalletAsset(program, assetType, file)
      .then(() => dispatch(getProgramWithSlug(program.slug)))
      .finally(() => setIsLoading(false))
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Flex vertical className="w-full" gap={8}>
        <Space className="w-full space-between">
          <Title level={5} className="m-0">
            {title}
          </Title>
          <Button
            style={{ border: 0 }}
            type="primary"
            loading={isLoading}
            htmlType="submit"
          >
            Save
          </Button>
        </Space>
        <div
          {...handlers}
          style={{
            borderRadius: 8,
            outline: `2px solid ${isActive ? HIGHLIGHT_COLOR : 'transparent'}`,
            outlineOffset: 2,
            transition: 'outline-color 120ms ease',
          }}
        >
          <Card>
            <Flex vertical gap={12}>
              <AttachmentEditor
                formItem={{ name: 'file', rules: [requiredRule()] }}
                dragger={{ fileTypes: ['picture'], multiple: false }}
                image={image}
                noStyle
              />
              <Card styles={{ body: { padding: 10 } }}>
                <Flex vertical gap={8}>
                  {tags?.length ? <WalletFieldTags tags={tags} /> : null}
                  <Text type="secondary">
                    {hint ? `${sizeHint} · ${hint}` : sizeHint}
                  </Text>
                </Flex>
              </Card>
            </Flex>
          </Card>
        </div>
      </Flex>
    </Form>
  )
}

export default WalletAssetUploader
